import { useEffect, useState, useRef, useCallback } from "react";
import { logout } from "../utils/helpers";
import {
  Dialog,
  DialogContent,
  Stack,
  Typography,
  Button,
} from "@mui/material";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";

const events = [
  "load",
  "mousemove",
  "mousedown",
  "click",
  "scroll",
  "keypress",
];

// Set timeouts for inactivity and the warning countdown
const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const WARNING_COUNTDOWN_S = 2; // 2 seconds

const AutoLogout = ({ children }) => {
  const [isWarningOpen, setWarningOpen] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_COUNTDOWN_S);

  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);

  const logoutAction = useCallback(() => {
    logout();
    window.location.href = "/login";
  }, []);

  const showWarningModal = useCallback(() => {
    setWarningOpen(true);
    setCountdown(WARNING_COUNTDOWN_S); // Reset countdown

    warningTimerRef.current = setInterval(() => {
      setCountdown((prevCountdown) => {
        if (prevCountdown <= 1) {
          clearInterval(warningTimerRef.current);
          logoutAction();
          return 0;
        }
        return prevCountdown - 1;
      });
    }, 1000);
  }, [logoutAction]);

  const startInactivityTimer = useCallback(() => {
    inactivityTimerRef.current = setTimeout(
      showWarningModal,
      INACTIVITY_TIMEOUT_MS,
    );
  }, [showWarningModal]);

  const resetInactivityTimer = useCallback(() => {
    clearTimeout(inactivityTimerRef.current);
    startInactivityTimer();
  }, [startInactivityTimer]);

  const handleStayLoggedIn = () => {
    setWarningOpen(false);
    clearInterval(warningTimerRef.current);
    resetInactivityTimer();
  };

  useEffect(() => {
    // Set up event listeners for user activity
    events.forEach((event) => {
      window.addEventListener(event, resetInactivityTimer);
    });

    // Start the initial inactivity timer
    startInactivityTimer();

    // Cleanup function to remove listeners and timers on unmount
    return () => {
      clearTimeout(inactivityTimerRef.current);
      clearTimeout(warningTimerRef.current);
      events.forEach((event) => {
        window.removeEventListener(event, resetInactivityTimer);
      });
    };
  }, [resetInactivityTimer, startInactivityTimer]);

  return (
    <div>
      <Dialog open={isWarningOpen}>
        <DialogContent sx={{ p: 4, width: { xs: "auto", sm: "400px" } }}>
          <Stack justifyContent={"center"} alignItems={"center"} spacing={2}>
            <ReportProblemRoundedIcon
              color="warning"
              sx={{ fontSize: "120px" }}
            />
            <Typography variant="h5" align="center" fontWeight="bold">
              Inactivity Warning
            </Typography>
            <Typography variant="body1" align="center">
              For your security, you will be logged out in {countdown} seconds.
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 3, width: "100%" }}>
              <Button onClick={logoutAction} variant="outlined" fullWidth>
                Logout Now
              </Button>
              <Button
                onClick={handleStayLoggedIn}
                variant="contained"
                autoFocus
                fullWidth
              >
                Stay Signed In
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
      {children}
    </div>
  );
};

export default AutoLogout;
